import { useFormWithStateContext } from 'contexts/FormWithStateContext';
import { CurrencyCode } from 'further-types/lp/vehicle';
import NumberInput from 'further-ui/components/NumberInput';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { useParams } from 'react-router-dom';
import {
  CapitalActionFormState,
  UpdateCapitalActionFormValues,
} from '../../../hooks/useCapitalActionForm';

type CapitalBreakdownItem = {
  commitmentId: string;
  fundsCalled?: {
    fromCommitmentLcy: number;
    fromRecallableDistributionLcy: number;
  };
};

type Props = {
  vehicleId: string;
  currency: CurrencyCode;
  capitalBreakdown: Array<CapitalBreakdownItem>;
  onChange: (allocations: Array<CapitalBreakdownItem>) => void;
};

const AdvanceSubscriptionAllocations: React.FC<Props> = ({
  vehicleId,
  currency,
  capitalBreakdown,
  onChange,
}) => {
  const { id: actionId } = useParams<{ id?: string }>();

  const {
    formMethods: { getValues },
  } = useFormWithStateContext<
    UpdateCapitalActionFormValues,
    CapitalActionFormState
  >();

  const date = getValues('ledgerDate');

  const [{ commitments }] = api.allocations.getCommitments.useSuspenseQuery({
    vehicleId,
    actionIdsToExclude: actionId ? [actionId] : [],
    date,
  });

  const handleAmountChange = (commitmentId: string, value: number) => {
    onChange(
      commitments.map((c) => {
        const existing = capitalBreakdown.find(
          (item) => item.commitmentId === c.commitmentId,
        );
        const amountLcy =
          c.commitmentId === commitmentId
            ? value
            : (existing?.fundsCalled?.fromCommitmentLcy ?? 0) +
              (existing?.fundsCalled?.fromRecallableDistributionLcy ?? 0);

        return {
          commitmentId: c.commitmentId,
          fundsCalled: {
            fromCommitmentLcy: amountLcy,
            fromRecallableDistributionLcy: 0,
          },
        };
      }),
    );
  };

  const rows = commitments.map((commitment) => {
    const breakdown = capitalBreakdown.find(
      (b) => b.commitmentId === commitment.commitmentId,
    );
    return {
      id: commitment.commitmentId,
      commitmentId: commitment.commitmentId,
      investorName: commitment.investorName,
      totalCommitmentLcy: commitment.totalCommitmentLcy,
      unallocatedCommitmentLcy: commitment.unallocatedCommitmentLcy,
      closeDate: commitment.closeDate,
      amountLcy:
        (breakdown?.fundsCalled?.fromCommitmentLcy ?? 0) +
        (breakdown?.fundsCalled?.fromRecallableDistributionLcy ?? 0),
    };
  });

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      label: 'Investor',
      key: 'investorName',
    },
    {
      label: 'Total commitment',
      key: 'totalCommitmentLcy',
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, { currency }),
    },
    {
      label: 'Unallocated commitment',
      key: 'unallocatedCommitmentLcy',
      render: (row) =>
        numberToCurrencyString(row.unallocatedCommitmentLcy, { currency }),
    },
    {
      label: 'Close date',
      key: 'closeDate',
      render: (row) => row.closeDate?.toReadable() ?? '-',
    },
    {
      label: 'Advance subscription payment',
      key: 'amountLcy',
      render: (row) => (
        <NumberInput
          value={row.amountLcy}
          decimals={2}
          currency={currency}
          onChange={(value) => {
            handleAmountChange(row.commitmentId, value ?? 0);
          }}
          clearZeroValueOnFocus
        />
      ),
    },
  ];

  return (
    <Table title="Advance subscription payment" rows={rows} columns={columns} />
  );
};

export default AdvanceSubscriptionAllocations;
